<template>
    <div @drop.prevent @dragover.prevent>
        <div v-if="!uploading" class="upload-component">
            <div class="card upload-card" @drop="handleFileDrop" @click="$refs.input.click()">
                <div class="card-body">
                    <div class="card-text">
                        <div>
                            <p><b>Drag and drop a file here to upload or, click inside box to browse</b></p>
                            <hr>
                            <p><small>Supported Filetypes: <i>ppt, dotm, ppsx, xls, pdf, pps, xlsx, xlt, docm, pptm, xltm, dotx, potm, ppsm, xltx, potx, doc, dot, xlsm, pot, pptx, docx</i></small></p>
                            <p><small>Max File Size: 100MB</small></p>
                        </div>
                    </div>

                    <input
                        ref="input"
                        v-on:change="handleFileInput"
                        name="file"
                        type="file"
                        accept=".ppt,.dotm,.ppsx,.xls,.pdf,.pps,.xlsx,.xlt,.docm,.pptm,.xltm,.dotx,.potm,.ppsm,.xltx,.potx,.doc,.dot,.xlsm,.pot,.pptx,.docx"
                        required
                    />
                </div>
                <ul v-if="file" class="list-group" @click.stop>
                    <li class="list-group-item d-flex justify-content-between align-items-center">
                        <span>{{file.name}}  -  <b>{{file.size | fileSize}}</b> MB</span>
                        <button @click.stop="handleRemoveFile" title="Remove FIle" class="remove-btn"><span>&times;</span></button>
                    </li>
                </ul>
            </div>

            <div class="output-format-field">
                <label for="output-format">Output Format:</label>
                <select id="output-format" ref="outputFormat">
                    <option value="PNG" selected>PNG</option>
                    <option value="PDF">PDF</option>
                </select>
            </div>

            <div class="page-range-field">
                <label for="page-range">Page Range (Optional):</label>
                <input id="page-range" ref="pageRange" type="text" class="form-control" aria-label="Page Range" placeholder="i.e. 2,6-8,10" />
            </div>

            <div class="watermark-field">
                <label for="watermark">Watermark Text (Optional):</label>
                <input id="watermark" ref="watermark" type="text" class="form-control" aria-label="Watermark Text" />
            </div>

            <div class="serverless-field form-check form-switch">
                <label for="serverless">Use Serverless Converter:</label>
                <input id="serverless" ref="serverless" type="checkbox" class="form-check-input" aria-label="Use Serverless Converter" />
            </div>
        </div>


        <div v-else class="progress-component">
            <div class="progress">
                <div
                    class="progress-bar progress-bar-striped"
                    :class="!finished ? 'progress-bar-animated' : ''"
                    role="progressbar"
                    :aria-valuenow="uploadPercentage"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :style="{ width: uploadPercentage + '%' }"
                ></div>
            </div>

            <p>
                {{
                    uploadPercentage < 100 ? "Uploading..."
                    : finished ? "Conversion Completed"
                    : "Running the Conversion..."
                }}
            </p>
        </div>

        <div v-if="error" class="alert alert-danger alert-dismissible fade show error-card" role="alert">
            <strong>Errors encountered while processing file:</strong>
            <ul>
                <li v-for="(message, index) in errorMessages" :key="index">{{message}}</li>
            </ul>
            <a v-if="outputLog" :href="outputLog"><i class="fas fa-download"></i> Download full output log</a>
        </div>

        <button
            v-if="!uploading"
            v-on:click="startConversion"
            :disabled="!file || uploading || error"
            class="btn btn-primary"
        >
            Start Conversion <i class="fas fa-angle-right"></i>
        </button>
    </div>
</template>

<script>
export default {
    props: [],
    data() {
        return {
            file: null,
            uploading: false,
            finished: false,
            error: false,
            errorMessages: [],
            outputLog: null,
            uploadPercentage: 0
        };
    },
    methods: {
        handleFileDrop(e) {
            const files = Array.from(e.dataTransfer.files);
            this.file = files[0];

            this.uploading = false;
            this.finished = false;
            this.error = false;
        },
        handleFileInput(e) {
            const files = Array.from(e.target.files);
            this.file = files[0];

            this.uploading = false;
            this.finished = false;
            this.error = false;
        },
        handleRemoveFile() {
            this.file = null;

            this.uploading = false;
            this.finished = false;
            this.error = false;
        },
        handleError(errorMessages = null) {
            this.uploading = false;
            this.error = true;
            this.uploadPercentage = 0;
            if (typeof errorMessages !== 'array') {
                this.errorMessages = [errorMessages];
            } else {
                this.errorMessages = errorMessages;
            }
        },
        async startConversion() {
            if (!this.file) return;

            // Construct the config for the job
            const config = {
                'outputFileFormat': this.$refs.outputFormat.value,
                'pageRange': this.$refs.pageRange.value.length > 0 ? this.$refs.pageRange.value : null,
                'watermark': this.$refs.watermark.value.length > 0 ? {
                    'watermarks': {
                        'type': 'text',
                        'text_lines': [
                            {
                                'line_text': [
                                    {
                                        'text': this.$refs.watermark.value
                                    }
                                ]
                            }
                        ]
                    }
                } : {},
                'serverless': this.$refs.serverless.checked
            };

            // Set uploading bool to true
            this.uploading = true;
            this.error = false;

            // Verify that the selected file is valid
            await this.isValidFile(this.file).catch(err => {
                return this.handleError(['Please select a file to upload. It looks like you tried selecting a directory']);
            });

            // Upload the document
            const { status: uploadStatus, data: uploadData} = await this.postUpload(this.file);
            if (uploadStatus !== 200) {
                this.handleError(uploadData.error);
                return;
            }
            console.log('DocID:', uploadData.docid);

            // Start the conversion job
            const { status: processStatus, data: processData} = await this.postProcess(uploadData.docid, config);
            if (processStatus !== 200) {
                this.handleError(processData.error);
                return;
            }
            console.log('JobID:', processData.jobid);

            // Wait for the job status to change from 'processing'
            const status = await new Promise(resolve => {
                const statusInterval = setInterval(async () => {
                    const { status: uploadStatus, data: statusData } = await this.getStatus(processData.jobid);
                    if (uploadStatus === 200 && (statusData.status !== 'processing' || statusData.pagecount > 0 && statusData.pages.length > 0)) {
                        clearInterval(statusInterval);
                        resolve(statusData);
                    }
                }, 1000);
            });
            
            // Handle result accordingly
            if (status.status !== 'error') {
                // If this was a PDF conversion redirect directly to the result, otherwise open viewer
                if (config.outputFileFormat === 'PDF') {
                    window.location = status.pages[0];
                } else {
                    window.location = `/view/${processData.jobid}`;
                }
            } else {
                console.log('Conversion failed');
                this.handleError(status.error);
            }
        },
        isValidFile(file) {
            return new Promise((resolve, reject) => {
                if (file.type !== '') {
                    resolve(file);
                }

                // Attempt to read the file. If there is an error while reading then it isn't a file
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (reader.error) {
                        reject(reader.error.name);
                    }
                    resolve(file);
                }
                reader.readAsBinaryString(file);
            })
        },
        async postUpload(file) {
            const formData = new FormData();
            formData.append('file', file);
            const postConfig = {
                onUploadProgress: e => {
                    this.uploadPercentage = parseInt(
                        Math.round((e.loaded / e.total) * 100)
                    );
                }
            };

            return await axios.post('/api/upload', formData, postConfig)
                .catch(err => { return err.response });
        },
        async postProcess(docid, config) {
            return await axios.post(`/api/${docid}/process`, config)
                .catch(err => { return err.response });
        },
        async getStatus(jobid) {
            return await axios.get(`/api/${jobid}/status`)
                .catch(err => { return err.response });
        }
    },
    filters: {
        fileSize: function(bytes) {
            const mb = bytes / 1000000;
            return mb.toFixed(1);
        }
    }
};
</script>
